import type { Ref } from 'react';

import { useTranslation } from '@/i18n/useTranslation';

export interface DocumentsSentenceProps {
  /** Show the QR overlay. Nothing else — the step's own controls are untouched by this one. */
  readonly onOpen: () => void;
  /** The control, so the step can return focus to it when the overlay closes. */
  readonly controlRef?: Ref<HTMLButtonElement>;
}

/**
 * ISS's documents sentence, verbatim, with *please click here* as its control [decisions 0341, 0359 ruling
 * 3; Figma · `Acceptable documents — USCIS link` in `03 · I-9 info`, read live 2026-10-01].
 *
 * ⭐ **The sentence is ONE dictionary string**, and the control is found inside it rather than the sentence
 * being cut into fragments around a placeholder: ISS typed it, and it ships byte for byte [0359 ruling 3 —
 * *"do not re-punctuate, do not tighten, do not split it"*]. A Spanish kiosk shows it in English, as stored,
 * until ISS supplies the Spanish [0359 ruling 2; 0328].
 *
 * ⭐ **The control is a `<button>`, never an `<a>`.** It opens an overlay on this screen and navigates
 * nowhere [0370] — an anchor would announce a link and promise a page. The frame's own text style carries a
 * hyperlink to USCIS; it is not built, because following it is exactly what 0370 removed.
 *
 * The frame: 14/24 regular `--muted-foreground`, left-aligned, full width; the control 500 weight,
 * underlined, `#005d68` (`--primary-muted-foreground`, 7.59:1 on the card). The full stop after it stays
 * outside it, as drawn.
 */
export function DocumentsSentence({ onOpen, controlRef }: DocumentsSentenceProps) {
  const t = useTranslation();
  const sentence = t('i9.documents.sentence');
  const label = t('i9.documents.linkLabel');
  const at = sentence.lastIndexOf(label);

  /*
   * 44px tall without moving a line: a `<button>` is an atomic inline, so the 10px of padding each way that
   * makes the touch target [skill § 9] is taken back by the same negative margin, and the line box keeps the
   * frame's 24px. The padding is invisible — it only widens where a finger lands.
   */
  const control = (text: string) => (
    <button
      aria-haspopup="dialog"
      className="kiosk-focusable -my-[10px] py-[10px] font-medium text-[var(--primary-muted-foreground)] underline"
      onClick={onOpen}
      ref={controlRef}
      type="button"
    >
      {text}
    </button>
  );

  return (
    <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
      {at === -1 ? (
        // Unreachable while the dictionary test holds; if it ever fails, the control must not vanish.
        control(sentence)
      ) : (
        <>
          {sentence.slice(0, at)}
          {control(label)}
          {sentence.slice(at + label.length)}
        </>
      )}
    </p>
  );
}
